import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import MacJitLogo from "../components/MacJitLogo";
import { Check, IndianRupee, Lock, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

export default function ShopPaymentPage() {
  const { saleId } = useParams();
  const nav = useNavigate();
  const [sale, setSale] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/shop/sales/${saleId}`)
      .then(async (r) => {
        const params = new URLSearchParams(window.location.search);
        const paidByRazorpay = params.get("razorpay_payment_link_status") === "paid" || params.get("razorpay_payment_id");
        if (!r.data?.paid && paidByRazorpay) {
          const paid = await api.post(`/public/shop/sales/${saleId}/pay`, {
            payment_method: "Razorpay",
            razorpay_payment_id: params.get("razorpay_payment_id") || "",
            razorpay_payment_link_id: params.get("razorpay_payment_link_id") || "",
          });
          window.history.replaceState({}, document.title, window.location.pathname);
          setSale(paid.data);
          toast.success("Payment received");
          return;
        }
        setSale(r.data);
      })
      .catch(() => toast.error("Payment link not found"));
  }, [saleId]);

  const pay = async () => {
    const hostedLink = sale?.razorpay_payment_link_url || (String(sale?.payment_link || "").startsWith("http") ? sale.payment_link : "");
    if (hostedLink) {
      window.location.href = hostedLink;
      return;
    }
    toast.error(sale?.razorpay_payment_link_error || "Online payment is not configured for this bill. Contact the merchant.");
  };

  if (!sale) {
    return <div className="min-h-screen bg-zinc-50 text-zinc-600 grid place-items-center font-mono text-sm">Loading payment link...</div>;
  }

  const items = sale.items || [];
  const billingAddons = sale.billing_addons || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-zinc-50 to-zinc-100 text-zinc-950">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MacJitLogo size={34} />
            <span className="font-display font-black text-xl tracking-tighter">MACJIT GARAGE</span>
          </div>
          <div className="flex items-center gap-1.5 text-zinc-500">
            <Lock className="w-3 h-3" />
            <span className="font-mono text-[10px] uppercase tracking-widest">Shop payment</span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10">
        {sale.paid ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500 flex items-center justify-center mb-6">
              <Check className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
            <h1 className="font-display font-black text-4xl tracking-tighter">Payment received</h1>
            <p className="text-zinc-600 mt-3 font-mono text-sm">Thank you for shopping at MacJit Garage.</p>
            <button onClick={() => nav("/")} className="mt-10 font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-black underline underline-offset-8">Back to MacJit</button>
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center gap-2">
              <span className="bg-orange-500 text-black font-mono text-[10px] uppercase tracking-widest font-bold px-2 py-0.5">Shop Bill</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">#{saleId.slice(0, 8).toUpperCase()}</span>
            </div>
            <h1 className="font-display font-black text-5xl tracking-tighter">Pay for parts</h1>
            <p className="text-zinc-500 mt-1 font-mono text-sm">{sale.customer_name || "Walk-in Customer"} · {sale.customer_phone || "No phone"}</p>

            <div className="mt-8 bg-white border border-zinc-200 shadow-sm">
              <div className="p-6">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
                  <ShoppingCart className="w-3 h-3" /> Items
                </p>
                {items.map((it) => (
                  <div key={it.inventory_id || it.name} className="flex justify-between py-2 text-sm border-b border-zinc-100 last:border-0">
                    <span>{it.name} <span className="text-zinc-400">x {it.qty}</span></span>
                    <span className="font-mono">₹{it.subtotal}</span>
                  </div>
                ))}
                {Number(sale.items_subtotal || 0) > 0 && (
                  <div className="border-t border-zinc-200 mt-3 pt-3 flex justify-between py-2 text-sm text-zinc-500">
                    <span>Subtotal</span>
                    <span className="font-mono">₹{sale.items_subtotal}</span>
                  </div>
                )}
                {billingAddons.map((line, idx) => (
                  <div key={line.key || `${line.label}-${idx}`} className="flex justify-between py-2 text-sm text-orange-600">
                    <span>{line.label}</span>
                    <span className="font-mono">₹{line.amount}</span>
                  </div>
                ))}
              </div>
              <div className="bg-black text-white p-6 flex items-end justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Total payable</p>
                  <p className="font-display font-black text-5xl tracking-tighter mt-1 flex items-center"><IndianRupee className="w-9 h-9 text-orange-500" />{sale.total}</p>
                </div>
              </div>
            </div>

            <button
              onClick={pay}
              disabled={busy}
              className="w-full mt-6 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-black font-display font-black uppercase tracking-widest py-5 transition-colors text-lg border-b-4 border-orange-700 active:translate-y-1 active:border-b-0"
            >
              {busy ? "Processing..." : `Pay ₹${sale.total} now`}
            </button>
          </>
        )}
      </main>
    </div>
  );
}
